<script setup lang="ts">
import { Plus as PlusIcon } from '@lucide/vue'
import { ref } from 'vue'

/**
 * Floating action button. Single-root <button> so aria/listeners fall through; `rootEl` is exposed
 * for callers that position a menu off it. `md` = 40px (desktop), `lg` = 48px (mobile). Default slot
 * is a Plus icon; override via slot.
 * - `variant="primary"` (default) — emerald brand fill, for the create "+".
 * - `variant="neutral"` — white surface, for over-map secondary actions like the back button.
 * - `variant="recording"` — red fill, replaces the create "+" while a track recording is running.
 */
interface Props {
  size?: 'md' | 'lg'
  variant?: 'primary' | 'neutral' | 'recording'
}
const props = withDefaults(defineProps<Props>(), { size: 'md', variant: 'primary' })

const rootEl = ref<HTMLButtonElement | null>(null)
defineExpose({ rootEl })

const sizeClass = { md: 'h-10 w-10', lg: 'h-12 w-12' }
const iconClass = { md: 'h-[18px] w-[18px]', lg: 'h-[22px] w-[22px]' }
const variantClass = {
  primary: 'bg-primary-700 text-white ring-1 ring-primary-900/10 hover:bg-primary-600',
  neutral: 'bg-white text-neutral-700 ring-1 ring-black/5 hover:bg-neutral-50',
  recording: 'bg-red-600 text-white ring-1 ring-red-900/15 hover:bg-red-500',
}
</script>

<template>
  <button
    ref="rootEl"
    type="button"
    class="flex items-center justify-center rounded-full shadow-md transition-colors hover:cursor-pointer"
    :class="[sizeClass[props.size], variantClass[props.variant]]"
  >
    <slot><PlusIcon :class="iconClass[props.size]" /></slot>
  </button>
</template>
